// packages/components/src/support-ai/TicketSummary.tsx
import React, { useState } from 'react';
import { Card, Badge, IconButton } from '@dopomogai/ui';
import { ExternalLink, MessageSquare, AlertCircle, CheckCircle2, Languages } from 'lucide-react';

interface TicketSummaryProps {
  initialQuery: string;
  translatedInitialQuery?: string;
  status: 'Open' | 'Pending' | 'Resolved' | 'Escalated';
  interactionCount: number;
}

const statusMap = {
  Open: { color: 'blue' as const, icon: <MessageSquare size={14} /> },
  Pending: { color: 'yellow' as const, icon: <AlertCircle size={14} /> },
  Resolved: { color: 'green' as const, icon: <CheckCircle2 size={14} /> },
  Escalated: { color: 'red' as const, icon: <AlertCircle size={14} /> },
};

const InfoItem: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
    <div className="text-sm font-medium text-slate-800 mt-1">{children}</div>
  </div>
);

export const TicketSummary: React.FC<TicketSummaryProps> = ({
  initialQuery,
  translatedInitialQuery,
  status,
  interactionCount,
}) => {
  const [showTranslation, setShowTranslation] = useState(true);
  const canTranslate = !!translatedInitialQuery && initialQuery !== translatedInitialQuery;
  const textToShow = showTranslation ? translatedInitialQuery : initialQuery;
  const statusInfo = statusMap[status] || statusMap.Pending;

  return (
    <Card noHoverEffect noPadding>
      <div className="p-5">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold text-slate-900">Ticket Summary</h3>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              alert('Opening ticket details in Salesforce...');
            }}
            className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:underline"
          >
            View in Salesforce
            <ExternalLink size={14} />
          </a>
        </div>

        <div className="space-y-4">
          <InfoItem label="Initial Issue">
            <div className="flex justify-between items-start">
                <p className="italic text-slate-800 flex-grow pr-4">"{textToShow}"</p>
                {canTranslate && (
                    <IconButton
                      icon={<Languages size={14} />}
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowTranslation(s => !s)}
                      tooltip={showTranslation ? "Show Original" : "Show Translation"}
                      // --- FIX: Add the missing ariaLabel for accessibility ---
                      ariaLabel={showTranslation ? "Show original language" : "Show English translation"}
                    />
                )}
            </div>
          </InfoItem>

          <div className="grid grid-cols-2 gap-4">
            <InfoItem label="Status">
              <Badge text={status} accentColor={statusInfo.color} icon={statusInfo.icon} />
            </InfoItem>
            <InfoItem label="Interactions">
              <p>{interactionCount}</p>
            </InfoItem>
          </div>
        </div>
      </div>
    </Card>
  );
};